/**
 * Feature Keys — Closed Registry (V1)
 *
 * Stable feature identifiers used across the Plan Feature Matrix.
 * Admins can ONLY toggle inclusion of these keys per plan; they cannot
 * create new keys. Keys are grouped into categories for the admin UI.
 *
 * IMPORTANT: keys here MUST stay in sync with the seed in
 * `platform_settings.plan_feature_matrix`.
 */

export const FEATURE_CATEGORIES = {
  CORE: "core",
  COMMERCE: "commerce",
  EMAILS: "emails",
  SESSIONS_PRO: "sessions_pro",
  QUIZ_PRO: "quiz_pro",
  GROWTH_PRO: "growth_pro",
};

/**
 * `core`: features that are part of every plan (admin cannot disable).
 * `lockable`: admin may toggle per plan from the matrix screen.
 */
export const FEATURE_KEYS = {
  // ── core (always-on, both plans) ──────────────────────────────────────────
  ACADEMY_SITE: "academy_site",
  UNLIMITED_COURSES: "unlimited_courses",
  UNLIMITED_STUDENTS: "unlimited_students",
  VIDEO_LESSONS: "video_lessons",
  FILE_LESSONS: "file_lessons",
  TEXT_LESSONS: "text_lessons",
  PROGRESS_TRACKING: "progress_tracking",
  DRIP_CONTENT: "drip_content",
  BRANDING: "branding",
  PAGE_BUILDER_ACADEMY: "page_builder_academy",
  PAGE_BUILDER_PRODUCT: "page_builder_product",
  PAGE_BUILDER_COURSE: "page_builder_course",
  TEAM_MANAGEMENT: "team_management",
  MESSAGING: "messaging",
  GROUP_MESSAGING: "group_messaging",
  NOTIFICATION_HISTORY: "notification_history",
  SENT_INVITES: "sent_invites",
  RECEIPTS: "receipts",
  COUPONS: "coupons",

  // ── commerce (Basic by default) ───────────────────────────────────────────
  STRIPE_PAYMENTS: "stripe_payments",
  BANK_TRANSFER: "bank_transfer",
  ONE_TIME_PRODUCTS: "one_time_products",
  BUNDLES: "bundles",
  DIGITAL_PRODUCTS: "digital_products",
  RECURRING_PRODUCTS: "recurring_products",

  // ── emails (Basic by default per approved decision) ───────────────────────
  AUTOMATED_EMAILS: "automated_emails",
  EMAIL_TEMPLATES: "email_templates",
  EMAIL_CAMPAIGNS: "email_campaigns",
  EMAIL_LOGS: "email_logs",

  // ── certificates (Pro per approved decision) ──────────────────────────────
  CERTIFICATES_AUTO: "certificates_auto",
  CERTIFICATES_MANUAL: "certificates_manual",
  CERTIFICATES_PRO_EXTRAS: "certificates_pro_extras",

  // ── sessions (Pro) ────────────────────────────────────────────────────────
  LIVE_SESSIONS: "live_sessions",
  RECURRING_SESSIONS: "recurring_sessions",
  SESSION_PACKAGES: "session_packages",
  SESSION_LANDING: "session_landing",
  SESSIONS_CATALOG: "sessions_catalog",

  // ── quiz (Pro) ────────────────────────────────────────────────────────────
  QUIZZES: "quizzes",
  AI_QUIZZES: "ai_quizzes",
  QUESTION_BANK: "question_bank",
  QUIZ_BLOCKING: "quiz_blocking",

  // ── growth (Pro) ──────────────────────────────────────────────────────────
  LIMITED_FREE_ACCESS: "limited_free_access",
  VIP_MEMBERSHIP: "vip_membership",
  GRANTS_AND_INVITES: "grants_and_invites",
  COURSE_DISCUSSIONS: "course_discussions",
  BANNERS: "banners",
  CUSTOM_DOMAIN: "custom_domain",
  GOOGLE_CALENDAR_SYNC: "google_calendar_sync",
  SEO_LANDING: "seo_landing",
  HIDE_DWRRAH_ATTRIBUTION: "hide_dwrrah_attribution",
  CUSTOM_HTML_BLOCK: "custom_html_block",
  HTML_TEMPLATES: "html_templates",
  ADVANCED_ANALYTICS: "advanced_analytics",
  TRACKING_INTEGRATIONS: "tracking_integrations",
};

/**
 * Core keys are protected from being toggled-off by the admin matrix UI.
 * They are guaranteed-on for every plan.
 */
export const CORE_FEATURE_KEYS = new Set([
  FEATURE_KEYS.ACADEMY_SITE,
  FEATURE_KEYS.UNLIMITED_COURSES,
  FEATURE_KEYS.UNLIMITED_STUDENTS,
  FEATURE_KEYS.VIDEO_LESSONS,
  FEATURE_KEYS.FILE_LESSONS,
  FEATURE_KEYS.TEXT_LESSONS,
  FEATURE_KEYS.PROGRESS_TRACKING,
  FEATURE_KEYS.DRIP_CONTENT,
  FEATURE_KEYS.BRANDING,
  FEATURE_KEYS.PAGE_BUILDER_ACADEMY,
  FEATURE_KEYS.PAGE_BUILDER_PRODUCT,
  FEATURE_KEYS.PAGE_BUILDER_COURSE,
  FEATURE_KEYS.TEAM_MANAGEMENT,
  FEATURE_KEYS.MESSAGING,
  FEATURE_KEYS.GROUP_MESSAGING,
  FEATURE_KEYS.NOTIFICATION_HISTORY,
  FEATURE_KEYS.SENT_INVITES,
  FEATURE_KEYS.RECEIPTS,
  FEATURE_KEYS.COUPONS,
  FEATURE_KEYS.STRIPE_PAYMENTS,
  FEATURE_KEYS.BANK_TRANSFER,
  FEATURE_KEYS.ONE_TIME_PRODUCTS,
  FEATURE_KEYS.BUNDLES,
  FEATURE_KEYS.DIGITAL_PRODUCTS,
]);

export const ALL_FEATURE_KEYS = Object.values(FEATURE_KEYS);

export function isValidFeatureKey(key) {
  return ALL_FEATURE_KEYS.includes(key);
}